import { useEffect, useState } from "react";
import {
  Download,
  CreditCard,
  TrendingDown,
  Calendar,
  AlertTriangle,
  Search,
  Filter,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { apiFetch } from "@/lib/api";

interface Loan {
  id: number;
  loan_amount: number;
  due_date: string;
  loan_days: number;
  status: string;
  defaulted_days: number;
  penalty_charged: number;
  paid: number;
  total_due: number;
  balance: number;
}

interface ScheduleItem {
  id: number;
  installment: number;
  amount: number;
  paid_amount: number;
  date: string;
  balance: number;
}

interface Payment {
  id: number;
  created_at: string;
  transaction_code: string;
  transaction_amount: number;
  loan_id: number;
}

export default function LoanStatementPage() {
  const [loan, setLoan] = useState<Loan | null>(null);
  const [schedule, setSchedule] = useState<ScheduleItem[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [search, setSearch] = useState("");

  const [loadingLoan, setLoadingLoan] = useState(true);
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [loadingPayments, setLoadingPayments] = useState(false);

  // --- Load loan summary first ---
  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoadingLoan(true);
        const res = await apiFetch<{ loan: Loan }>("/loan-summary");
        if (!alive) return;
        setLoan(res.loan);
      } catch (e) {
        console.error(e);
      } finally {
        if (alive) setLoadingLoan(false);
      }
    })();

    return () => { alive = false; };
  }, []);

  // --- Load repayment schedule after loan is fetched ---
  useEffect(() => {
    if (!loan) return;

    let alive = true;

    (async () => {
      try {
        setLoadingSchedule(true);
        const res = await apiFetch<{ data: ScheduleItem[] }>("/loan-schedule");
        if (!alive) return;
        setSchedule(res.data || []);
      } catch (e) {
        console.error(e);
      } finally {
        if (alive) setLoadingSchedule(false);
      }
    })();

    return () => { alive = false; };
  }, [loan]);

  // --- Load payments after schedule ---
  useEffect(() => {
    if (!loan) return;

    let alive = true;

    (async () => {
      try {
        setLoadingPayments(true);
        const res = await apiFetch<{ data: Payment[] }>("/loan-payments");
        if (!alive) return;
        setPayments(res.data || []);
      } catch (e) {
        console.error(e);
      } finally {
        if (alive) setLoadingPayments(false);
      }
    })();

    return () => { alive = false; };
  }, [loan]);

  const filteredPayments = payments.filter((p) =>
    p.transaction_code.toLowerCase().includes(search.toLowerCase())
  );

  const statCards = loan
    ? [
        {
          label: "Loan Amount",
          value: `KSh ${loan.loan_amount.toLocaleString()}`,
          sub: `Status: ${loan.status}`,
          variant: "bg-green-600",
          icon: CreditCard,
        },
        {
          label: "Outstanding Balance",
          value: `KSh ${loan.balance.toLocaleString()}`,
          sub: "As at today",
          variant: "bg-blue-600",
          icon: TrendingDown,
        },
        {
          label: "Due Date",
          value: loan.due_date,
          sub: `Days: ${loan.loan_days}`,
          variant: "bg-orange-500",
          icon: Calendar,
        },
        {
          label: "Penalty",
          value: `KSh ${loan.penalty_charged.toLocaleString()}`,
          sub: `${loan.defaulted_days} days overdue`,
          variant: "bg-purple-600",
          icon: AlertTriangle,
        },
      ]
    : [];

  return (
    <div className="space-y-6 animate-fade-in">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Loan Statement</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Complete transaction history for your loan account
          </p>
        </div>
        <Button className="bg-primary hover:bg-primary/90 text-primary-foreground gap-2 hidden sm:flex">
          <Download className="w-4 h-4" />
          Download PDF
        </Button>
      </div>

      {/* --- Loan Summary Cards --- */}
      {loadingLoan ? (
        <div className="glass-card rounded-xl p-5 text-center">Loading summary...</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {statCards.map(({ label, value, sub, variant, icon: Icon }) => (
            <div key={label} className={`${variant} rounded-xl p-5 text-white shadow-lg`}>
              <div className="flex justify-between mb-2">
                <p className="text-xs uppercase opacity-80">{label}</p>
                <Icon className="w-5 h-5 opacity-80" />
              </div>
              <p className="text-2xl font-bold">{value}</p>
              <p className="text-xs opacity-80 mt-1">{sub}</p>
            </div>
          ))}
        </div>
      )}

      {/* --- Repayment Schedule --- */}
      <div className="glass-card rounded-xl p-5">
        <h2 className="font-semibold text-foreground mb-4 flex items-center gap-2">
          <span className="w-1 h-5 bg-primary rounded-full" />
          Repayment Schedule
        </h2>
        <div className="overflow-x-auto">
          {loadingSchedule ? (
            <p className="text-center py-4">Loading schedule...</p>
          ) : schedule.length ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  {["#", "Due Date", "Installment", "Paid Amount", "Balance", "Status"].map((h) => (
                    <th key={h} className="text-left text-muted-foreground font-medium py-2 pr-4 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {schedule.map((s, i) => {
                  const dueDate = new Date(s.date);
                  const paid = s.paid_amount >= s.amount;
                  const overdue = !paid && dueDate < new Date();
                  return (
                    <tr key={s.id} className="border-b border-border/50 hover:bg-muted/50 transition-colors">
                      <td className="py-2.5 pr-4 text-muted-foreground">{i + 1}</td>
                      <td className="py-2.5 pr-4 text-foreground whitespace-nowrap">{dueDate.toLocaleDateString("en-GB")}</td>
                      <td className="py-2.5 pr-4 font-medium text-foreground">KSh {s.amount.toLocaleString()}</td>
                      <td className="py-2.5 pr-4 font-medium text-green-card">KSh {s.paid_amount.toLocaleString()}</td>
                      <td className="py-2.5 pr-4 text-foreground font-medium">KSh {s.balance.toLocaleString()}</td>
                      <td className="py-2.5">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          paid ? "bg-green-card/15 text-green-card" : overdue ? "bg-destructive/15 text-destructive" : "bg-muted text-muted-foreground"
                        }`}>
                          {paid ? "✓ Paid" : overdue ? "Overdue" : "Pending"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <p className="text-center py-4">No repayment schedule found.</p>
          )}
        </div>
      </div>

      {/* --- Payment History --- */}
      <div className="glass-card rounded-xl p-5">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
          <h2 className="font-semibold text-foreground flex items-center gap-2">
            <span className="w-1 h-5 bg-primary rounded-full" />
            Transaction History
          </h2>
          <div className="flex gap-2 w-full sm:w-auto">
            <div className="relative flex-1 sm:w-48">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 bg-muted border-border text-foreground placeholder:text-muted-foreground h-9 text-sm"
              />
            </div>
            <Button size="sm" variant="outline" className="border-border text-foreground hover:bg-accent gap-1">
              <Filter className="w-3 h-3" />
              Filter
            </Button>
          </div>
        </div>

        <div className="overflow-x-auto">
          {loadingPayments ? (
            <p className="text-center py-4">Loading payments...</p>
          ) : filteredPayments.length ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  {["Date", "Reference", "Amount", "Balance", "Status"].map((h) => (
                    <th key={h} className="text-left text-muted-foreground font-medium py-2 pr-4 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredPayments.map((p) => (
                  <tr key={p.id} className="border-b border-border/50 hover:bg-muted/50 transition-colors">
                    <td className="py-3 pr-4 text-muted-foreground whitespace-nowrap">{new Date(p.created_at).toLocaleDateString("en-GB")}</td>
                    <td className="py-3 pr-4 font-mono text-xs text-foreground">{p.transaction_code}</td>
                    <td className={`py-3 pr-4 font-semibold whitespace-nowrap ${p.transaction_amount < 0 ? "text-blue-card" : "text-green-card"}`}>
                      KSh {Math.abs(p.transaction_amount).toLocaleString()}
                    </td>
                    <td className="py-3 pr-4 text-foreground font-medium">{loan ? `KSh ${loan.balance.toLocaleString()}` : "—"}</td>
                    <td className="py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        p.transaction_amount > 0 ? "bg-green-card/15 text-green-card" : "bg-muted text-muted-foreground"
                      }`}>
                        {p.transaction_amount > 0 ? "✓ Paid" : "Pending"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-center py-4">No payments found.</p>
          )}
        </div>
      </div>
    </div>
  );
}